import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import {
    Close,
    ExpandLess,
    ExpandMore,
    InfoOutlined,
    ReportProblemOutlined,
} from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

/**
 * Known Meta / worker codes → short layman titles.
 * From API_DOCUMENT.md Error Codes.
 */
const ERROR_BY_CODE = {
    131049: "WhatsApp limited delivery",
    131047: "Too many messages / chat window closed",
    80007: "Temporary WhatsApp issue",
    132000: "Invalid recipient number",
    132001: "Invalid message template",
    132005: "Template not approved yet",
    132012: "Wrong template values / parameters",
    132015: "Wrong template values / parameters",
    100: "Invalid request parameters",
    131021: "Recipient blocked your number",
    131026: "Recipient opted out",
    131014: "Invalid phone number",
    190: "WhatsApp account disabled",
    40001: "Invalid phone number",
};

/**
 * Match free-text reasons (when code is missing or unknown) to short titles.
 * Order matters — more specific phrases first.
 */
const ERROR_BY_REASON = [
    {
        match:
            /invalid parameter|parameter(?:s)? (?:do(?:es)? not|don't) match|parameter mismatch|parameter format|# of parameters|number of parameters|expected .*parameter|wrong (?:number of )?parameter|missing parameter|body_param|header_param/i,
        title: "Wrong template values / parameters",
    },
    {
        match: /part of an experiment/i,
        title: "Number in a WhatsApp experiment",
    },
    {
        match: /spam rate limit|flagged as spam|too many previous messages were blocked/i,
        title: "Spam / sending limit reached",
    },
    {
        match: /unsettled payments|business eligibility payment|billing_hub/i,
        title: "Payment / billing issue",
    },
    {
        match: /stop receiving marketing|chosen to stop receiving marketing/i,
        title: "Stopped marketing messages",
    },
    {
        match: /unsupported image mime|media upload error|mime type/i,
        title: "Media file not supported",
    },
    {
        match: /ssl connection timeout|curl error/i,
        title: "Connection timeout",
    },
    {
        match: /maintain healthy ecosystem|engagement/i,
        title: "WhatsApp limited delivery",
    },
    {
        match: /opted out|undeliverable/i,
        title: "Recipient opted out",
    },
    {
        match: /invalid phone|empty, non-digit|exceeds max length/i,
        title: "Invalid phone number",
    },
    {
        match: /rate limit|24-hour/i,
        title: "Too many messages / chat window closed",
    },
    {
        match: /template not approved/i,
        title: "Template not approved yet",
    },
    {
        match: /blocked/i,
        title: "Recipient blocked your number",
    },
    {
        match: /something went wrong/i,
        title: "Unknown WhatsApp error",
    },
];

const getShare = (count, total) => {
    if (!total || total <= 0) return 0;
    return Math.round((count / total) * 1000) / 10;
};

const formatCode = (errorCode) =>
    errorCode === null || errorCode === undefined || errorCode === ""
        ? "—"
        : String(errorCode);

const PREVIEW_LIMIT = 3;
export const FAILED_SUMMARY_STATE_KEY = "wbMessageQueueFailedSummary";

const categorizeError = (row) => {
    const code = row?.error_code;
    const reason = (row?.reason || "").trim();
    const numericCode = code === null || code === undefined || code === "" ? null : Number(code);

    // Prefer reason-based “invalid parameters” match even when a generic code like 100 is present
    for (const entry of ERROR_BY_REASON) {
        if (entry.match.test(reason)) {
            return { title: entry.title, code: formatCode(code) };
        }
    }

    if (numericCode != null && !Number.isNaN(numericCode) && ERROR_BY_CODE[numericCode]) {
        return { title: ERROR_BY_CODE[numericCode], code: formatCode(code) };
    }

    if (code != null && ERROR_BY_CODE[code]) {
        return { title: ERROR_BY_CODE[code], code: formatCode(code) };
    }

    return { title: "Other delivery failure", code: formatCode(code) };
};

const FailedMessageSummaryCard = ({ failedSummary = [], failedTotal, showAll = false }) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(true);
    const [detailRow, setDetailRow] = useState(null);

    const rows = Array.isArray(failedSummary) ? failedSummary : [];
    const sortedRows = useMemo(
        () => [...rows].sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0)),
        [rows]
    );
    const totalFromRows = sortedRows.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
    const displayTotal =
        failedTotal != null && failedTotal !== ""
            ? Number(failedTotal) || 0
            : totalFromRows;
    const hasFailures = displayTotal > 0 || sortedRows.length > 0;
    const visibleRows = showAll ? sortedRows : sortedRows.slice(0, PREVIEW_LIMIT);
    const hiddenCount = Math.max(sortedRows.length - PREVIEW_LIMIT, 0);

    const openAllFailures = (event) => {
        event.stopPropagation();
        const payload = { failedSummary: sortedRows, failedTotal: displayTotal };
        sessionStorage.setItem(FAILED_SUMMARY_STATE_KEY, JSON.stringify(payload));
        navigate("/whatsapp-business/message-queue/failures", { state: payload });
    };

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    ...WB_SX.card,
                    mb: 2.5,
                    borderColor: WB_UI.color.border,
                    overflow: "hidden",
                }}
            >
                <Box
                    onClick={() => setExpanded((prev) => !prev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setExpanded((prev) => !prev);
                        }
                    }}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 2.5,
                        py: 1.75,
                        cursor: "pointer",
                        userSelect: "none",
                        backgroundColor: "#f8fafc",
                        borderBottom: expanded ? `1px solid ${WB_UI.color.border}` : "none",
                        "&:hover": { backgroundColor: "#f1f5f9" },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                                backgroundColor: "#fff",
                                color: "#64748b",
                                border: `1px solid ${WB_UI.color.border}`,
                            }}
                        >
                            <ReportProblemOutlined fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ ...WB_SX.sectionTitle, mb: 0.25 }}>
                                Failed Message Summary
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {hasFailures
                                    ? expanded
                                        ? "Why messages failed (simple view)"
                                        : `${sortedRows.length} issue type${sortedRows.length === 1 ? "" : "s"} · click to expand`
                                    : "No failures in this date range"}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <Chip
                            label={`${displayTotal.toLocaleString()} failed`}
                            size="small"
                            sx={{
                                height: 28,
                                fontWeight: 700,
                                backgroundColor: "#e2e8f0",
                                color: "#0f172a",
                                border: "1px solid #cbd5e1",
                            }}
                        />
                        <IconButton
                            size="small"
                            tabIndex={-1}
                            sx={{
                                color: "#64748b",
                                border: `1px solid ${WB_UI.color.border}`,
                                borderRadius: 1.5,
                                backgroundColor: "#fff",
                            }}
                        >
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2.5, py: 2, backgroundColor: "#fafbfc" }}>
                        {!hasFailures ? (
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                No failed messages in the selected date range.
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {visibleRows.map((row, index) => {
                                    const count = Number(row.count) || 0;
                                    const share = getShare(count, displayTotal);
                                    const category = categorizeError(row);

                                    return (
                                        <Box
                                            key={`cat-${index}`}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 2,
                                                flexWrap: "wrap",
                                                px: 2,
                                                py: 1.35,
                                                borderRadius: 2,
                                                backgroundColor: "#fff",
                                                border: `1px solid ${WB_UI.color.border}`,
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                                <Typography variant="body2" fontWeight={700} color="text.primary">
                                                    {category.title}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        mt: 0.35,
                                                        color: "#94a3b8",
                                                        fontFamily: "ui-monospace, Menlo, monospace",
                                                        fontSize: "0.7rem",
                                                    }}
                                                >
                                                    Error code: {category.code}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.5,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Box sx={{ textAlign: "right" }}>
                                                    <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                                                        {count.toLocaleString()}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        fontWeight={600}
                                                    >
                                                        {share.toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<InfoOutlined sx={{ fontSize: 15 }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDetailRow({ ...row, category });
                                                    }}
                                                    sx={{
                                                        ...WB_SX.secondaryButton,
                                                        textTransform: "none",
                                                        whiteSpace: "nowrap",
                                                        px: 1.25,
                                                        py: 0.4,
                                                        fontSize: "0.75rem",
                                                    }}
                                                >
                                                    View detail
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                })}
                                {!showAll && hiddenCount > 0 && (
                                    <Button
                                        variant="text"
                                        onClick={openAllFailures}
                                        sx={{
                                            alignSelf: "flex-start",
                                            textTransform: "none",
                                            fontWeight: 700,
                                            px: 0.5,
                                        }}
                                    >
                                        View more
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Card>

            <Dialog
                open={Boolean(detailRow)}
                onClose={() => setDetailRow(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2, border: `1px solid ${WB_UI.color.border}` },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 1.5,
                        borderBottom: `1px solid ${WB_UI.color.border}`,
                        backgroundColor: "#f8fafc",
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ pr: 1 }}>
                        {detailRow?.category?.title || "Failure detail"}
                    </Typography>
                    <IconButton size="small" onClick={() => setDetailRow(null)}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Chip
                            size="small"
                            label={`${(Number(detailRow?.count) || 0).toLocaleString()} messages`}
                            sx={{ fontWeight: 700, backgroundColor: "#e2e8f0" }}
                        />
                        <Chip
                            size="small"
                            label={`${getShare(Number(detailRow?.count) || 0, displayTotal).toFixed(1)}% of failed`}
                            sx={{ fontWeight: 700, backgroundColor: "#e2e8f0" }}
                        />
                        <Chip
                            size="small"
                            label={`Code: ${detailRow?.category?.code || "—"}`}
                            sx={{
                                fontWeight: 700,
                                fontFamily: "ui-monospace, Menlo, monospace",
                                backgroundColor: "#e2e8f0",
                            }}
                        />
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{
                            display: "block",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            color: "#94a3b8",
                            mb: 0.75,
                        }}
                    >
                        Full message
                    </Typography>
                    <Box
                        sx={{
                            p: 1.75,
                            borderRadius: 2,
                            backgroundColor: "#f8fafc",
                            border: `1px solid ${WB_UI.color.border}`,
                        }}
                    >
                        <Typography variant="body2" sx={{ lineHeight: 1.55, color: "#334155" }}>
                            {detailRow?.reason || "No detailed message available"}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setDetailRow(null)}
                        sx={WB_SX.primaryButton}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FailedMessageSummaryCard;
